// LegacyBridge.js - VERSIÓN CORREGIDA CON FIREBASE REAL
// Bridge de compatibilidad para migración de datos legacy a Firebase

class LegacyBridge {
  constructor() {
    this.dbName = 'imoveispro_db';
    this.userEmail = localStorage.getItem('userEmail') || 'admin@imoveispro.com';
    this.migrationMode = localStorage.getItem('use_legacy_bridge') === 'true';
    this.firebaseReady = false;
    this.dataContext = null;
    this.firebaseStrategy = null;
    
    // Verificar Firebase
    this.checkFirebase();
  }

  // ✅ Verificar si Firebase está disponible - VERSIÓN CORREGIDA
  async checkFirebase() {
    try {
      console.log('🔄 Verificando conexión Firebase...');
      
      // Primero intentar cargar DataContext
      try {
        const module = await import('/src/lib/data/context/DataContext.js');
        if (module && module.getDataContext) {
          this.dataContext = module.getDataContext();
          console.log('✅ DataContext cargado');
        }
      } catch (error) {
        console.warn('⚠️ DataContext no disponible:', error.message);
      }
      
      // Intentar cargar CloudStrategy directamente
      try {
        const strategyModule = await import('/src/lib/data/strategies/CloudStrategy.js');
        if (strategyModule && strategyModule.default) {
          this.firebaseStrategy = new strategyModule.default('1'); // userId hardcodeado por ahora
          console.log('✅ CloudStrategy creada');
          this.firebaseReady = true;
          console.log('🔥 Firebase listo para usar');
        }
      } catch (error) {
        console.warn('⚠️ CloudStrategy no disponible:', error.message);
      }
      
      // Si ninguna funcionó, usar modo simulación
      if (!this.firebaseReady) {
        console.warn('⚠️ Firebase no disponible - usando modo simulación');
      }
      
    } catch (error) {
      console.warn('⚠️ Error verificando Firebase (modo simulación):', error.message);
      this.firebaseReady = false;
    }
    
    return { firebaseReady: this.firebaseReady };
  }

  // ✅ Obtener datos de imoveispro_db
  async getLegacyData() {
    console.log('🔍 Obteniendo datos legacy de imoveispro_db...');
    
    const dbRaw = localStorage.getItem(this.dbName);
    if (!dbRaw) {
      console.warn('⚠️ No se encontró imoveispro_db en localStorage');
      return this.getEmptyCollections();
    }

    try {
      const db = JSON.parse(dbRaw);
      
      const user = db.users?.find(u => u.email === this.userEmail) || db.users?.[0];
      const currentUserId = user?.id || '1';
      
      console.log(`👤 Usuario identificado: ${this.userEmail} (ID: ${currentUserId})`);
      
      const filteredData = {
        users: db.users || [],
        clients: (db.clients || []).filter(c => c.userId == currentUserId),
        properties: (db.properties || []).filter(p => p.userId == currentUserId),
        tasks: (db.tasks || []).filter(t => t.userId == currentUserId),
        calendarEvents: (db.calendarEvents || []).filter(e => e.userId == currentUserId),
        documents: (db.documents || []).filter(d => d.userId == currentUserId),
        financeiro: (db.financeiro || []).filter(f => f.userId == currentUserId),
        company: db.company ? [db.company] : [],
        interactions: db.interactions || []
      };

      console.log('📊 ESTADÍSTICAS DE DATOS:');
      Object.entries(filteredData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          console.log(`  • ${key}: ${value.length} registros`);
        }
      });

      return filteredData;

    } catch (error) {
      console.error('❌ Error al procesar datos legacy:', error);
      return this.getEmptyCollections();
    }
  }

  // ✅ Migrar a Firebase REAL - VERSIÓN CORREGIDA
  async migrateToFirebase(collectionName, data) {
    try {
      console.log(`🔥 Enviando ${data.length} registros a Firebase...`);
      
      // Verificar Firebase antes de proceder
      if (!this.firebaseReady || !this.firebaseStrategy) {
        console.warn('⚠️ Firebase no disponible - usando modo simulación');
        return { 
          success: true, 
          saved: data.length, 
          errors: 0,
          simulated: true 
        };
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      console.log(`📤 Usando CloudStrategy para ${collectionName}...`);
      
      for (const item of data) {
        try {
          // Preparar datos para Firebase
          const firebaseItem = {
            ...item,
            _migratedAt: new Date().toISOString(),
            _migrationSource: 'legacy_system',
            _legacyId: item.id // Guardar ID original
          };
          
          // Eliminar ID si existe (Firebase genera su propio ID)
          delete firebaseItem.id;
          
          // Usar CloudStrategy para crear en Firebase
          const result = await this.firebaseStrategy.create(collectionName, firebaseItem);
          
          successCount++;
          console.log(`  ✅ Registro guardado en Firebase: ${result.id}`);
          
        } catch (error) {
          console.error(`  ❌ Error guardando en Firebase:`, error.message);
          errorCount++;
        }
      }
      
      const result = {
        success: errorCount === 0,
        saved: successCount,
        errors: errorCount,
        collection: collectionName,
        timestamp: new Date().toISOString()
      };
      
      console.log(`🎉 Resultado migración ${collectionName}:`, result);
      return result;
      
    } catch (error) {
      console.error('❌ Error en migrateToFirebase:', error);
      return { 
        success: false, 
        saved: 0, 
        errors: 1,
        error: error.message 
      };
    }
  }

  // ✅ Migrar datos REALES a Firebase
  async migrateCollection(collectionName, transformer) {
    console.log(`🚀 Iniciando migración REAL de: ${collectionName}`);
    
    const legacyData = await this.getLegacyData();
    const collectionData = legacyData[collectionName] || [];
    
    if (collectionData.length === 0) {
      console.log(`📭 ${collectionName}: Sin datos para migrar`);
      return { 
        success: true, 
        count: 0, 
        message: 'No hay datos para migrar'
      };
    }

    console.log(`📦 ${collectionName}: Migrando ${collectionData.length} registros REALES...`);
    
    const modernData = [];
    const errors = [];
    
    for (const legacyItem of collectionData) {
      try {
        const modernItem = transformer.toModern(legacyItem);
        modernData.push(modernItem);
        console.log(`  ✓ Transformado: ${modernItem.name || modernItem.nome || modernItem.id || 'Sin nombre'}`);
      } catch (error) {
        console.error(`  ✗ Error transformando:`, error);
        errors.push({
          item: legacyItem,
          error: error.message
        });
      }
    }

    if (modernData.length === 0 && errors.length > 0) {
      return { 
        success: false, 
        count: 0, 
        message: 'Todos los registros fallaron en transformación'
      };
    }

    const firebaseResult = await this.migrateToFirebase(collectionName, modernData);
    
    if (firebaseResult.saved > 0) {
      console.log(`✅ ${collectionName}: ${firebaseResult.saved} registros migrados a Firebase`);
      
      if (firebaseResult.success) {
        this.markCollectionAsMigrated(collectionName);
      }
      
      return {
        success: firebaseResult.success,
        count: firebaseResult.saved,
        errors: firebaseResult.errors,
        simulated: firebaseResult.simulated || false,
        message: firebaseResult.success 
          ? `✅ Migración completa: ${firebaseResult.saved} registros guardados en Firebase`
          : `⚠️ Migración parcial: ${firebaseResult.saved} ok, ${firebaseResult.errors} errores`
      };
    } else {
      console.log(`❌ ${collectionName}: Falló la migración a Firebase`);
      
      return {
        success: false,
        count: 0,
        errors: firebaseResult.errors,
        message: '❌ Falló la conexión con Firebase'
      };
    }
  }

  // ✅ Obtener estado de migración
  getMigrationStatus() {
    const migrated = JSON.parse(localStorage.getItem('migrated_collections') || '{}');
    return migrated;
  }

  // ✅ Verificar si una colección está migrada
  isCollectionMigrated(collectionName) {
    const status = this.getMigrationStatus();
    return !!status[collectionName];
  }

  // ✅ Revertir migración de una colección
  async revertMigration(collectionName) {
    console.log(`↩️ Revertiendo migración de: ${collectionName}`);
    
    const migrated = JSON.parse(localStorage.getItem('migrated_collections') || '{}');
    delete migrated[collectionName];
    localStorage.setItem('migrated_collections', JSON.stringify(migrated));
    
    console.log(`✅ ${collectionName}: Estado de migración revertido`);
    return { success: true, message: 'Estado de migración revertido' };
  }

  // ✅ Marcar colección como migrada
  markCollectionAsMigrated(collectionName) {
    const migrated = JSON.parse(localStorage.getItem('migrated_collections') || '{}');
    migrated[collectionName] = {
      migratedAt: new Date().toISOString(),
      version: '1.0',
      records: this.getCollectionCount(collectionName),
      firebase: true
    };
    localStorage.setItem('migrated_collections', JSON.stringify(migrated));
    
    console.log(`🏷️ ${collectionName}: Marcada como migrada en Firebase`);
  }

  // ✅ Obtener conteo de una colección
  async getCollectionCount(collectionName) {
    const data = await this.getLegacyData();
    return Array.isArray(data[collectionName]) ? data[collectionName].length : 0;
  }

  // ✅ Obtener todas las estadísticas
  async getMigrationStats() {
    const data = await this.getLegacyData();
    const migrated = this.getMigrationStatus();
    
    const stats = {};
    let totalLegacy = 0;
    let totalMigrated = 0;
    
    Object.keys(data).forEach(key => {
      if (Array.isArray(data[key])) {
        const count = data[key].length;
        const isMigrated = migrated[key];
        
        stats[key] = {
          count,
          migrated: isMigrated,
          status: isMigrated ? 'MODERN' : 'LEGACY',
          canMigrate: count > 0 && !isMigrated
        };
        
        totalLegacy += count;
        if (isMigrated) totalMigrated += count;
      }
    });
    
    if (data.company && data.company.length > 0) {
      const count = data.company.length;
      const isMigrated = migrated.company;
      
      stats.company = {
        count,
        migrated: isMigrated,
        status: isMigrated ? 'MODERN' : 'LEGACY',
        canMigrate: !isMigrated
      };
      
      totalLegacy += count;
      if (isMigrated) totalMigrated += count;
    }
    
    return {
      stats,
      totals: {
        legacy: totalLegacy,
        migrated: totalMigrated,
        collections: Object.keys(stats).length
      },
      bridgeMode: this.migrationMode,
      firebaseReady: this.firebaseReady,
      firebaseStrategy: !!this.firebaseStrategy
    };
  }

  // ✅ Colecciones vacías
  getEmptyCollections() {
    return {
      users: [],
      clients: [],
      properties: [],
      tasks: [],
      calendarEvents: [],
      documents: [],
      financeiro: [],
      company: [],
      interactions: []
    };
  }

  // ✅ Obtener transformadores por defecto
  getDefaultTransformers() {
    return {
      clients: {
        toModern: (legacy) => ({
          ...legacy,
          id: legacy.id || `client_${Date.now()}`,
          userId: legacy.userId || '1',
          createdAt: legacy.createdAt || new Date().toISOString(),
          updatedAt: legacy.updatedAt || new Date().toISOString(),
          status: legacy.status || 'active',
          _migration: {
            source: 'legacy_system',
            version: '1.0',
            migratedAt: new Date().toISOString()
          }
        }),
        toLegacy: (modern) => {
          const { _migration, ...legacy } = modern;
          return legacy;
        }
      },
      properties: {
        toModern: (legacy) => ({
          ...legacy,
          id: legacy.id || `property_${Date.now()}`,
          userId: legacy.userId || '1',
          createdAt: legacy.createdAt || new Date().toISOString(),
          updatedAt: legacy.updatedAt || new Date().toISOString(),
          status: legacy.status || 'Disponível',
          _migration: {
            source: 'legacy_system',
            version: '1.0',
            migratedAt: new Date().toISOString()
          }
        }),
        toLegacy: (modern) => {
          const { _migration, ...legacy } = modern;
          return legacy;
        }
      },
      users: {
        toModern: (legacy) => ({
          ...legacy,
          id: legacy.id || `user_${Date.now()}`,
          createdAt: legacy.createdAt || new Date().toISOString(),
          updatedAt: legacy.updatedAt || new Date().toISOString(),
          role: legacy.role || 'user',
          _migration: {
            source: 'legacy_system',
            version: '1.0',
            migratedAt: new Date().toISOString()
          }
        }),
        toLegacy: (modern) => {
          const { _migration, ...legacy } = modern;
          return legacy;
        }
      },
      company: {
        toModern: (legacy) => ({
          ...legacy,
          id: 'company_config',
          userId: legacy.userId || '1',
          createdAt: legacy.createdAt || new Date().toISOString(),
          updatedAt: legacy.updatedAt || new Date().toISOString(),
          _migration: {
            source: 'legacy_system',
            version: '1.0',
            migratedAt: new Date().toISOString()
          }
        }),
        toLegacy: (modern) => {
          const { _migration, ...legacy } = modern;
          return legacy;
        }
      }
    };
  }

  // ✅ Probar conexión con Firebase - VERSIÓN CORREGIDA
  async testFirebaseConnection() {
    try {
      console.log('🧪 Probando conexión Firebase REAL...');
      
      // Intentar crear un documento de prueba
      if (this.firebaseReady && this.firebaseStrategy) {
        const testData = {
          test: true,
          message: 'Prueba de conexión LegacyBridge',
          timestamp: new Date().toISOString()
        };
        
        console.log('📝 Creando documento de prueba...');
        const result = await this.firebaseStrategy.create('migration_test', testData);
        
        console.log('✅ Documento de prueba creado:', result.id);
        
        return {
          connected: true,
          strategy: 'CloudStrategy',
          testId: result.id,
          message: '✅ Firebase funcionando correctamente',
          timestamp: new Date().toISOString()
        };
      } else {
        await this.checkFirebase();
        
        return {
          connected: this.firebaseReady,
          strategy: this.firebaseStrategy ? 'CloudStrategy' : 'None',
          message: this.firebaseReady 
            ? '✅ Firebase conectado' 
            : '❌ Firebase no disponible',
          timestamp: new Date().toISOString()
        };
      }
      
    } catch (error) {
      console.error('❌ Error en testFirebaseConnection:', error);
      return {
        connected: false,
        error: error.message,
        message: '❌ Error conectando con Firebase'
      };
    }
  }
}

// ✅ EXPORTACIÓN CORREGIDA
// ============================================
// EXPORTACIONES - VERSIÓN CORREGIDA
// ============================================

// Exportar función getLegacyBridge
export function getLegacyBridge() {
  if (!window.legacyBridgeInstance) {
    window.legacyBridgeInstance = new LegacyBridge();
  }
  return window.legacyBridgeInstance;
}

// Exportar la clase también
export { LegacyBridge };

// Exportar como default para compatibilidad
export default LegacyBridge;